import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Eye, EyeOff } from "lucide-react";

const EMPTY_POST = { title: "", category: "", summary: "", content: "", author: "GACA Editorial", image_url: "", tags: [] };

function BlogPostForm({ editingPost, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    editingPost
      ? { title: editingPost.title, category: editingPost.category, summary: editingPost.summary, content: editingPost.content || "", author: editingPost.author, image_url: editingPost.image_url || "", tags: editingPost.tags || [] }
      : EMPTY_POST
  );
  const [tagInput, setTagInput] = useState("");

  const addTag = () => {
    if (tagInput && !form.tags.includes(tagInput)) {
      setForm({ ...form, tags: [...form.tags, tagInput] });
      setTagInput("");
    }
  };
  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter((x) => x !== tag) });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-[#0B2545]/10 p-6 mb-4 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Edit size={14} className="text-[#13A8BD]" />
        <span className="text-xs font-bold text-[#0B2545] uppercase tracking-wider">{editingPost ? "Edit Post" : "New Post"}</span>
      </div>
      <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none h-10 text-sm font-bold" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-none h-9 text-xs" />
        <Input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="rounded-none h-9 text-xs" />
      </div>
      <Input placeholder="Featured Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none h-9 text-xs" data-testid="post-image-url-input" />
      {form.image_url && (
        <div className="h-32 bg-[#F7F9FC] border border-[#0B2545]/10 flex items-center justify-center overflow-hidden">
          <img src={form.image_url} alt="Preview" className="h-full w-auto object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
      <Input placeholder="Summary (shown in preview)" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-none h-9 text-xs" />
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Content</span>
          <span className="text-[9px] text-slate-300">Supports Markdown: **bold**, *italic*, ## headings, - lists</span>
        </div>
        <Textarea placeholder="Write your article content using Markdown formatting..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} className="rounded-none text-xs resize-none font-mono leading-relaxed" />
      </div>
      <div className="flex items-center gap-2">
        <Input placeholder="Add tag..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} className="rounded-none h-8 text-xs w-40" />
        <button type="button" onClick={addTag} className="text-xs text-[#13A8BD] font-bold">+ Tag</button>
        <div className="flex gap-1 flex-wrap">
          {form.tags.map((tag) => (
            <span key={tag} className="text-[10px] bg-[#13A8BD]/10 text-[#13A8BD] px-2 py-0.5 flex items-center gap-1">
              {tag} <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">&times;</button>
            </span>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="bg-[#0B2545] text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5">{editingPost ? "Update Post" : "Publish"}</button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 px-4 py-2.5">Cancel</button>
      </div>
    </form>
  );
}

function BlogPostItem({ post, canEdit, onTogglePublish, onEdit, onDelete }) {
  return (
    <div className="p-4 border-b border-[#0B2545]/10 last:border-b-0 hover:bg-[#F7F9FC]">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3 flex-1">
          {post.image_url && <img src={post.image_url} alt="" className="w-16 h-16 object-cover flex-shrink-0 border border-[#0B2545]/10" onError={(e) => { e.target.style.display = 'none'; }} />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-bold text-[#0B2545]">{post.title}</h3>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 ${post.published ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                {post.published ? 'Published' : 'Draft'}
              </span>
            </div>
            <p className="text-[10px] text-slate-500">{post.category} — {post.author} {post.tags?.length > 0 && `— Tags: ${post.tags.join(', ')}`}</p>
            <p className="text-xs text-slate-500 mt-1 line-clamp-1">{post.summary}</p>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button onClick={() => onTogglePublish(post)} className="text-slate-400 hover:text-[#13A8BD]" title={post.published ? "Unpublish" : "Publish"}>
              {post.published ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button onClick={() => onEdit(post)} className="text-slate-400 hover:text-[#0B2545]">
              <Edit size={14} />
            </button>
            <button onClick={() => onDelete(post.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogManager({ posts, setPosts, authApi, canEdit }) {
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const handleSubmit = async (form) => {
    if (editingPost) {
      await authApi().patch(`/blog/${editingPost.id}`, form);
      setPosts(posts.map((p) => p.id === editingPost.id ? { ...p, ...form } : p));
      setEditingPost(null);
      toast.success("Post updated.");
    } else {
      const res = await authApi().post("/blog", form);
      setPosts([res.data, ...posts]);
      toast.success("Post created.");
    }
    setShowForm(false);
  };

  const togglePublish = async (post) => {
    await authApi().patch(`/blog/${post.id}`, { published: !post.published });
    setPosts(posts.map((p) => p.id === post.id ? { ...p, published: !p.published } : p));
    toast.success(post.published ? "Unpublished." : "Published.");
  };

  const deletePost = async (id) => {
    await authApi().delete(`/blog/${id}`);
    setPosts(posts.filter((p) => p.id !== id));
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPost(null);
  };

  return (
    <div>
      {canEdit && (
        <div className="flex justify-end mb-4 gap-2">
          <button
            onClick={() => { setEditingPost(null); setShowForm(!showForm); }}
            data-testid="add-post-button"
            className="inline-flex items-center gap-1 bg-[#13A8BD] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-[#0e8a9c]"
          >
            <Plus size={14} /> {editingPost ? "Cancel Edit" : "New Post"}
          </button>
        </div>
      )}
      {showForm && (
        <BlogPostForm editingPost={editingPost} onSubmit={handleSubmit} onCancel={handleCancel} />
      )}
      <div className="bg-white border border-[#0B2545]/10">
        {posts.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No posts yet.</div>
        ) : (
          posts.map((p) => (
            <BlogPostItem key={p.id} post={p} canEdit={canEdit} onTogglePublish={togglePublish} onEdit={startEdit} onDelete={deletePost} />
          ))
        )}
      </div>
    </div>
  );
}
